// 11 §9 — walks an AnimationPlan with Phaser tweens (C7: slide, lunge, flash, sink…). Groups
// within a stage play in parallel; the next group starts when the longest clip ends. Reports
// progress to PlaybackStore and honours `skip`.
import { turnOf } from '../../core/playback/facing';
import type Phaser from 'phaser';
import type { AnimationPlan, Clip } from '../../core/playback/plan';
import { FLEE_FADE_MS, heldTag, LUNGE_SHARE, RISE_PX, SINK_PX, WARP_SQUARES } from '../../core/playback/catalogue';
import type { UnitSprite } from './UnitSprite';
import type { Effect, ResolutionEntry, Tile } from '../../core/sim/types';
import type { Escape } from '../../core/story/genesis';
import { playEscape, type EscapeHost } from './escape';
import { swayTag } from '../../core/sim/modifiers';
import { consumable } from '../../core/sim/items';
import { catalogueItem } from '../../core/items/catalogue';
import { gearPowerTag } from '../../core/items/describe';
import { BLESSED_TAG, findTag } from '../../core/explore/explore';
import { cssColour, statusInfo } from '../../core/sim/statuses';
import { clipBeats, type ClipBeats } from '../../core/audio/mix';
import { legsOf, walkKeys } from '../../core/playback/walk';
import type { SpellCast } from '../../core/playback/spells';
import type { CombatFx } from './CombatFx';
import type { FlightFx } from './FlightFx';
import { CARRY_DROP_K, CARRY_GRAB_K, CARRY_PEAK, carriedPose, crowSquares, flierOf, flightPeak, flightPose } from '../../core/playback/flight';
import { feetPoint } from '../../core/playback/walk';
import type { WaterFx } from '../water/WaterFx';
import { gaitKey, swims } from '../../core/sim/water';
import { killTweens } from './oneShots';

export interface EffectPlayerHost {
  unit(id: number): UnitSprite | undefined;
  tileCentre(t: Tile): { x: number; y: number };
  cell: number;
  floatText(t: Tile, text: string, colour: string, lift?: number): void;
  banner(text: string, ms: number): void;
  /** docs/22 O4: how this unit gets away instead of falling (the World's first named foe), if it does. */
  escape?(unitId: number): Escape | undefined;
  /** The board's effects layer and size, for the getaway's motes. */
  escapeHost?(speed: number): EscapeHost;
  /** docs/34: the server's cues for this clip, scheduled on its beats as it starts. */
  cues?(clip: Clip, beats: ClipBeats, speed: number): void;
  /** docs/34: a skipped playback stops the cues scheduled past the cut. */
  cancelCues?(): void;
  /** docs/41: a mechanism worked (a block slides, a rune lights) — the board's fixture layer shows it. */
  mechanism?(effect: Extract<Effect, { kind: 'Interacted' }>, ms: number, actor?: number): void;
  /** docs/47: draws a spell over `ms` (at the playing speed) landing at `impactMs`. */
  spell?(cast: SpellCast, ms: number, impactMs: number, speed: number): Promise<void>;
  /** docs/47: a skipped playback puts out every spell still burning. */
  clearSpells?(): void;
  /** docs/72 IT18: a weapon blow just landed (a magic weapon glints and strikes with its own sound). */
  blow?(actor: number, target: number): void;
  /** docs/56 EP4: shots in flight and blood. */
  combat?: CombatFx;
  /** docs/114 MT9: the ground reacted (the board's matter layer shows it and names it). */
  react?(effect: Extract<Effect, { kind: 'Reacted' }>, ms: number): void;
  /** docs/56 EP5: the last hero falls — the camera closes on it, the board shakes, the defeat sounds. */
  finale?(unitId: number, ms: number): void;
  /** docs/52: a hidden thing showed itself on its square, or a trap snapped there. */
  secret?(effect: Extract<Effect, { kind: 'Revealed' | 'TrapSprung' }>, ms: number): void;
  /** docs/56: a group's clips have played — what they did now stands on the board (and in the HUD). */
  landed?(entries: ResolutionEntry[]): void;
  /** docs/52: the name the party now knows a found thing or a trap by. */
  secretName?(fixture: number): string | undefined;
  /** docs/60: a crawl moment — a depth or chamber banner, a lair or roamer call-out, lore read, a shrine's answer, a fall. */
  crawl?(effect: Extract<Effect, { kind: 'AreaEntered' | 'LairWoke' | 'Roused' | 'LoreRead' | 'ShrineAnswered' | 'Fell' }>, ms: number): void;
  /** docs/61 EX1: a search's sweep over the squares it reached. */
  searched?(effect: Extract<Effect, { kind: 'Searched' }>, ms: number): void;
  /** docs/126: a hero took a fallen foe's pouch — the board's stash layer stops drawing it. */
  pickedUp?(fixture: number): void;
  /** docs/138 XO7: is `fixture` an exploration find (not a fallen foe's pouch)? */
  isFind?(fixture: number): boolean;
  /** docs/138 XO4: is `unit` one of my heroes (a find it picks up is mine)? */
  mine?(unit: number): boolean;
  /** docs/138 XO5: the wonder blesses `unit` — a soft glow and motes rising off it. */
  blessed?(unit: number, ms: number): void;
  /** docs/61: an exploration moment — a wonder beheld, a veil parted, a vantage, a way sighted, the party leaving. */
  explore?(effect: Extract<Effect, { kind: 'Discovered' | 'VeilParted' | 'Glimpsed' | 'WaySighted' | 'Departed' }>, ms: number): void;
  /** docs/66 RM10: a call or raising spoken (bubble + banner), or broken, or a captive freed. */
  call?(effect: Extract<Effect, { kind: 'Called' | 'Raised' | 'CallBroken' | 'RaiseBroken' | 'CaptiveFreed' | 'Retreat' }>, ms: number): void;
  /** docs/60 DC14: a unit stepped far across the board (a stair, a portal, a chute) — the camera may follow. */
  jumped?(unit: number, from: Tile, to: Tile, ms: number): void;
  /** docs/79: a huge body (`size` ≥ 2) set down on its square — the board thumps under it (docs/92: and it kicks up its ground). */
  thump?(size: number, unit?: UnitSprite): void;
  /** docs/101: a unit falls as its clip begins (the scene calls out my kill streak over the one who felled it). */
  dropped?(entry: ResolutionEntry, ms: number): void;
  /** docs/92 HM6: a huge body uses a move — it plays its moment (fire, wings, a roar, a smash, rock) and may say a line. */
  huge?(unit: UnitSprite, moveName: string, ms: number): void;
  /** docs/102: a flight's shadow, dust and air. */
  flight?: FlightFx;
  /** docs/93 AQ10: wakes, splashes and the swirl a withdrawing water foe leaves. */
  water?: WaterFx;
  /** A hero's figure by its hero id (who lent the hand, docs/93 AQ8). */
  heroUnit?(heroId: number): UnitSprite | undefined;
  /**
   * docs/140: a dungeon trap's moment — its spring (the camera, the card, a foe's cry), one beat of its
   * script drawn by its act, its end, a plate found or taken apart. `ms` at the playing speed.
   */
  snare?(effect: Extract<Effect, { kind: 'SnareSprung' | 'SnareActed' | 'SnareEnded' | 'SnareFound' | 'SnareDisarmed' }>, ms: number, entry: ResolutionEntry): void;
  /** Stereo placement of a unit's sounds. */
  panOf?(unit: number): number | undefined;
}


/** Floating-number colours (mockup 08): damage red, heal green, status bone, push gold. */
/** docs/69: a status's name rises in its own colour. */
const statusColour = (kind: string): string => { const i = statusInfo(kind); return i ? cssColour(i.colour) : FLOAT.status; };
const FLOAT = { hurt: '#ff5a4f', heal: '#6fe08a', status: '#eef0f6', push: '#f4cf78', sway: '#ffbf3b', move: '#ff9a5c', gear: '#c9a2ff', backstab: '#ffd34d', radiant: '#ffd34d', tempo: '#ffe28a' } as const;

/** docs/118 PT14b: the running part of a run-off clip of `ms` (the rest is its fade), at the playing `speed`. */
const runPart = (ms: number, speed: number): number => Math.max(1, ms - FLEE_FADE_MS / speed);

export class EffectPlayer {
  private cancelled = false;
  constructor(private readonly scene: Phaser.Scene, private readonly host: EffectPlayerHost) {}

  async play(plan: AnimationPlan, onStage: (i: number) => void, shouldSkip: () => boolean): Promise<void> {
    this.cancelled = false;
    const stop = () => shouldSkip() || this.cancelled;
    for (let s = 0; s < plan.stages.length; s++) {
      const stage = plan.stages[s]!;
      // docs/56 EP2: a beat before the other side starts acting
      if (stage.leadMs) { await this.wait(stage.leadMs / plan.speed, stop); }
      onStage(s);
      for (let g = 0; g < stage.groups.length; g++) {
        if (stop()) { this.jumpToEnd(plan); return; }
        await Promise.all(stage.groups[g]!.map((clip) => this.playClip(clip, plan.speed)));
        this.host.landed?.(stage.groups[g]!.map((c) => c.entry));
        // docs/56 EP2: and one after each blow, for its number and its blood
        if (stage.gaps[g]) await this.wait(stage.gaps[g]! / plan.speed, stop);
      }
    }
  }

  /** Waits `ms` of scene time, or less if the playback is skipped meanwhile. */
  wait(ms: number, stop: () => boolean = () => this.cancelled): Promise<void> {
    if (ms <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const end = this.scene.time.now + ms;
      const tick = this.scene.time.addEvent({ delay: 50, loop: true, callback: () => { if (stop() || this.scene.time.now >= end) { tick.remove(false); resolve(); } } });
    });
  }

  cancel(): void { this.cancelled = true; }

  private jumpToEnd(plan: AnimationPlan): void {
    // (and the one-shot flares and rings whose fades those tweens were, board/oneShots)
    killTweens(this.scene);
    this.host.cancelCues?.();
    this.host.clearSpells?.();
    this.host.combat?.clear();
    this.host.flight?.clear();
    this.host.water?.clear();
    for (const st of plan.stages) for (const g of st.groups) for (const clip of g) {
      const e = clip.entry.effect;
      if (e.kind === 'Moved') { const u = this.host.unit(e.unit); const last = e.path[e.path.length - 1]; if (u && last) u.placeAt(last, this.host.cell); }
      if (e.kind === 'Displaced' || e.kind === 'Fell') { const u = this.host.unit(e.unit); if (u) { u.placeAt(e.to, this.host.cell); u.setAlpha(1).setScale(1).setAngle(0); } }
      if (e.kind === 'Dropped') { const u = this.host.unit(e.unit); if (u && e.lies && !this.host.escape?.(e.unit)) u.fall(e.lies, this.host.cell, 0); else u?.setAlpha(0); }
      if (e.kind === 'Revived') this.host.unit(e.unit)?.setAlpha(1);
      // docs/118 PT14b: a runner skipped past is already gone
      if (e.kind === 'Left') { const u = this.host.unit(e.unit); const last = e.path[e.path.length - 1] ?? e.from; if (u) { u.placeAt(last, this.host.cell); u.setAlpha(0); } }
      if (e.kind === 'Held' && e.reason === 'Withdrew') this.host.unit(e.unit)?.setVisible(false);
    }
  }

  private playClip(clip: Clip, speed: number): Promise<void> {
    const ms = Math.max(1, clip.ms / speed);
    const e = clip.entry.effect;
    // the beats this clip's tweens hit (the target flashes 40 % into a lunge, 60 % into a bolt; a
    // walk's squares last as long as they cost), where the server's cues for it land
    // (a foe's spell clip already played its entry's cues: the entry's own clip stays quiet, docs/47)
    if (this.host.cues && !clip.muted) {
      // docs/102: a flight takes no steps (its wings and its landing are its sounds)
      const path = e.kind === 'Moved' && !clip.flight ? e.path : e.kind === 'Left' ? e.path : undefined;
      const costs = e.kind === 'Moved' || e.kind === 'Left' ? (e.costs && e.costs.length === e.path.length ? e.costs : e.path.map(() => 1)) : undefined;
      // docs/118 PT14b: a run's steps fall in its running part, before the fade
      const beats = clip.spell ? { impactMs: (clip.impactMs ?? ms * 0.6) / speed, endMs: ms, stepMs: [] }
        : e.kind === 'Left' ? { ...clipBeats('flee', runPart(ms, speed), path ? costs : undefined), endMs: ms }
        : clipBeats(clip.tween.kind, ms, path ? costs : undefined);
      this.host.cues(clip, beats, speed);
    }
    const done = () => new Promise<void>((r) => this.scene.time.delayedCall(ms, r));
    // the actor turns to the side its action goes as it begins (a walk overall, a blow or a mend at its target)
    const turn = turnOf(clip.entry, (id) => this.host.unit(id)?.tile);
    if (turn) this.host.unit(turn.unit)?.setFacing(turn.face);
    // docs/69: a status lands on the figure (or leaves it) as its entry plays, whatever its clip draws
    if (e.kind === 'StatusSet') this.host.unit(e.unit)?.aura.apply(e.status, e.magnitude);
    if (e.kind === 'Dropped') this.host.dropped?.(clip.entry, ms);
    if (e.kind === 'StatusCleared') this.host.unit(e.unit)?.aura.remove(e.status);
    // docs/102: an earlier flight drew this entry (a flier's next bound, the figure it carried off)
    if (clip.drawn) {
      const u = e.kind === 'Moved' || e.kind === 'Displaced' ? this.host.unit(e.unit) : undefined;
      const last = e.kind === 'Moved' ? e.path[e.path.length - 1] : e.kind === 'Displaced' ? e.to : undefined;
      if (u && last && (u.tile.x !== last.x || u.tile.y !== last.y)) u.placeAt(last, this.host.cell);
      return done();
    }
    // docs/93 AQ9: a water foe out of everyone's reach gives up the fight: it sinks away with a swirl
    if (e.kind === 'Held' && e.reason === 'Withdrew') {
      const u = this.host.unit(e.unit);
      if (u) {
        this.rise(e.unit, 'SINKS AWAY', FLOAT.status, 1);
        this.host.water?.swirl(feetPoint(u.tile, u.size, this.host.cell), u.size, ms);
        u.water.sinkAway(ms * 0.85);
      }
      return done();
    }
    if (clip.flight && e.kind === 'Moved') {
      const u = this.host.unit(e.unit);
      if (u) return this.fly(u, clip.flight, ms, e.blockedAt);
    }
    switch (clip.tween.kind) {
      case 'spell': {
        // docs/47: the spell's own clip — gathered, flown, landed — before the entries it caused
        if (!clip.spell || !this.host.spell) return done();
        return this.host.spell(clip.spell, ms, Math.max(1, (clip.impactMs ?? ms * 0.6) / speed), speed);
      }
      case 'slide': case 'push': {
        if (e.kind !== 'Moved' && e.kind !== 'Displaced') return done();
        const u = this.host.unit(e.unit);
        // docs/47: a Blink's arrival — the spell clip showed the trip; the figure is there
        if (clip.teleport && u) { const last = e.kind === 'Moved' ? e.path[e.path.length - 1] : e.to; if (last) u.placeAt(last, this.host.cell); u.setAlpha(1).setScale(1); return done(); }
        // docs/55, docs/60: a portal stone or a stair sets the figure down far away — it fades out where it
        // stood and in beside the other stone (the camera goes with it), never sliding across the board
        if (u && e.kind === 'Displaced' && Math.abs(e.to.x - e.from.x) + Math.abs(e.to.y - e.from.y) > WARP_SQUARES) return this.warp(u, e.from, e.to, ms);
        const walk = u ? legsOf(e, u.tile, ms) : undefined;
        if (!u || !walk || walk.legs.length === 0) return done();
        // docs/39 FW6: the figure walks the sim's route square by square — every leg along a row
        // or a column, as long as its step cost (mud a slog), facing the way the walk goes overall
        // (it turned as the clip began: `turnOf`).
        // A walk out of the fog starts where the sim says it did, not where the figure was last seen.
        if (walk.start.x !== u.tile.x || walk.start.y !== u.tile.y) u.placeAt(walk.start, this.host.cell);
        const shoved = e.kind === 'Displaced';
        if (shoved) this.rise(e.unit, this.shoveWord(e as Extract<Effect, { kind: 'Displaced' }>, u, clip.entry, ms), FLOAT.push);
        const n = walk.legs.length;
        // docs/53: a leap is one leg over several squares — the figure swells as it flies
        const leg0 = walk.legs[0]!;
        if (!shoved && n === 1 && Math.abs(leg0.tile.x - walk.start.x) + Math.abs(leg0.tile.y - walk.start.y) > 1) {
          this.scene.tweens.add({ targets: u, scaleX: 1.3, scaleY: 1.3, duration: Math.max(1, leg0.ms / 2), yoyo: true, ease: 'Sine.easeOut' });
        }
        return new Promise<void>((resolve) => {
          this.scene.tweens.chain({
            targets: u,
            // docs/79: a huge body walks its footprint's feet point, square to square, as a small one does
            tweens: walkKeys(walk.legs, u.size, this.host.cell).map((k, i) => ({
              x: k.x, y: k.y, duration: k.ms, ease: k.ease,
              onStart: () => { u.tile = { ...walk.legs[i]!.tile }; },
            })),
            onComplete: () => { u.placeAt(walk.legs[n - 1]!.tile, this.host.cell); if (e.blockedAt) this.bump(u, e.blockedAt); if (u.size >= 2) this.host.thump?.(u.size, u); resolve(); },
          });
        });
      }
      case 'flee': {
        // docs/118 PT14b: the foe runs its whole route off the field at a run — every runner at
        // once (they share a group) — and fades out on its edge square; a runner the party loses
        // in the fog fades where it was last seen (its route was cut to the squares in view)
        if (e.kind !== 'Left') return done();
        const u = this.host.unit(e.unit);
        if (!u) return done();
        const runMs = runPart(ms, speed);
        const walk = legsOf(e, u.tile, runMs);
        if (walk && (walk.start.x !== u.tile.x || walk.start.y !== u.tile.y)) u.placeAt(walk.start, this.host.cell);
        const fade = () => new Promise<void>((resolve) => {
          this.scene.tweens.add({ targets: u, alpha: 0, duration: Math.max(1, ms - runMs), ease: 'Quad.easeIn', onComplete: () => resolve() });
        });
        if (!walk || walk.legs.length === 0) return fade();
        const n = walk.legs.length;
        return new Promise<void>((resolve) => {
          this.scene.tweens.chain({
            targets: u,
            tweens: walkKeys(walk.legs, u.size, this.host.cell).map((k, i) => ({
              x: k.x, y: k.y, duration: k.ms, ease: 'Linear',
              onStart: () => { u.tile = { ...walk.legs[i]!.tile }; const f = walk.legs[i]!.face; if (f) u.setFacing(f); },
            })),
            onComplete: () => { u.placeAt(walk.legs[n - 1]!.tile, this.host.cell); void fade().then(resolve); },
          });
        });
      }
      case 'lunge': {
        if (e.kind !== 'Damaged') return done();
        const attacker = clip.entry.actor !== undefined ? this.host.unit(clip.entry.actor) : undefined;
        const target = this.host.unit(e.unit);
        if (attacker && target) this.strike(attacker, target, ms);
        // the blow lands 40 % in (the cues' impact beat, core/audio/mix)
        this.scene.time.delayedCall(ms * 0.4, () => { this.hurt(e.unit, e.dealt, attacker); if (e.dealt > 0 && clip.entry.actor !== undefined) this.host.blow?.(clip.entry.actor, e.unit); });
        return done();
      }
      case 'projectile': {
        if (e.kind !== 'Damaged') return done();
        const shooter = clip.entry.actor !== undefined ? this.host.unit(clip.entry.actor) : undefined;
        const target = this.host.unit(e.unit);
        if (shooter && target && this.host.combat) {
          // the shooter draws back (20 %), lets fly, and the shot lands 60 % in
          if (shooter.x !== target.x) shooter.setFacing(target.x > shooter.x ? 1 : -1);
          const back = this.toward(shooter, target, -this.host.cell * 0.08);
          this.scene.tweens.add({ targets: shooter, x: shooter.x + back.x, y: shooter.y + back.y, duration: ms * 0.18, yoyo: true, ease: 'Quad.easeOut' });
          const from = this.chest(shooter), to = this.chest(target);
          // the blow lands when the shot does (about 60 % in, the cues' impact beat)
          this.scene.time.delayedCall(ms * 0.2, () => this.host.combat!.shot(from, to, ms * 0.4, shooter.unit.faction === 'hero' ? 'arrow' : 'bolt', () => { this.hurt(e.unit, e.dealt, shooter); if (e.dealt > 0 && clip.entry.actor !== undefined) this.host.blow?.(clip.entry.actor, e.unit); }));
        } else this.scene.time.delayedCall(ms * 0.6, () => this.hurt(e.unit, e.dealt, shooter));
        return done();
      }
      case 'burst': case 'flash': {
        if (e.kind === 'Damaged') { const by = clip.entry.actor !== undefined ? this.host.unit(clip.entry.actor) : undefined; this.scene.time.delayedCall(ms * 0.6, () => this.hurt(e.unit, e.dealt, by)); }
        if (e.kind === 'Healed') this.rise(e.unit, `+${e.amount}`, FLOAT.heal);
        // docs/29: why the number just shown is not the usual one — "☀ −1" in amber
        if (e.kind === 'Swayed') this.rise(e.unit, swayTag(e.modifier, e.delta), FLOAT.sway, 1);
        // docs/72 IT3: a worn piece's power names itself over its wearer ("Last stand!", "Thorns 2")
        if (e.kind === 'GearPower') this.rise(e.unit, gearPowerTag(e.power, e.amount), FLOAT.gear, 1);
        // docs/180 RD2: the blow that would have dropped a hero above half its health
        if (e.kind === 'Glanced') this.rise(e.unit, e.landed > 0 ? 'GLANCING' : 'BLOCKED', FLOAT.heal, 1);
        // docs/134 HL2: a foe holding its ground says so, so standing still never reads as frozen
        if (e.kind === 'Held') { const tag = heldTag(e.reason); if (tag) this.rise(e.unit, tag, FLOAT.status, 1); }
        return done();
      }
      case 'pulse': {
        if (e.kind === 'Healed') this.rise(e.unit, `+${e.amount}`, FLOAT.heal);
        if (e.kind === 'StatusSet') this.rise(e.unit, `${(statusInfo(e.status)?.name ?? e.status).toUpperCase()}${e.magnitude ? ` ${e.magnitude}` : ''}`, statusColour(e.status));
        // docs/40: the spent item's name over its user (the heal or the status already rose)
        if (e.kind === 'ItemUsed') this.rise(e.unit, (consumable(e.def)?.name ?? catalogueItem(e.def)?.name ?? 'Item').toUpperCase(), FLOAT.push, 1);
        if (e.kind === 'ResourceGained' && e.amount > 0) this.rise(e.unit, `+${e.amount}`, FLOAT.status);
        // docs/52: what a search turned up, a trap made safe
        if (e.kind === 'Searched') {
          // docs/61 EX1: the sweep runs out over every square within reach the searcher can see
          this.host.searched?.(e, ms);
          this.rise(e.unit, e.found > 0 ? 'FOUND SOMETHING' : 'NOTHING HERE', e.found > 0 ? FLOAT.push : FLOAT.status, 1);
        }
        if (e.kind === 'Disarmed') this.rise(e.unit, 'DISARMED', FLOAT.heal, 1);
        // docs/126: what was in a fallen foe's pouch rises over the hero who took it
        if (e.kind === 'PickedUp') {
          this.host.pickedUp?.(e.fixture);
          const names = e.items.map((d) => consumable(d)?.name ?? catalogueItem(d)?.name ?? 'Item');
          if (this.host.isFind?.(e.fixture)) {
            // docs/138 XO4/XO7: a find is the picker's own — "+6 gold (yours)" over my hero
            this.rise(e.unit, findTag(e.gold, names, !!this.host.mine?.(e.unit)), FLOAT.radiant, 1);
          } else {
            const parts = [...(e.gold > 0 ? [`+${e.gold} GOLD`] : []), ...names.map((n) => n.toUpperCase())];
            this.rise(e.unit, parts.length ? parts.join(' · ') : 'EMPTY POUCH', FLOAT.push, 1);
          }
        }
        // docs/53: the foe calls its move as it begins — "TRAMPLING CHARGE!" in ember over it
        if (e.kind === 'MoveUsed') {
          this.rise(e.unit, `${e.name.toUpperCase()}!`, FLOAT.move, 1);
          const mover = this.host.unit(e.unit);
          if (mover?.huge) this.host.huge?.(mover, e.name, ms);
        }
        // docs/138 XO5: the wonder's blessing settles on the hero
        if (e.kind === 'Blessed') { this.host.blessed?.(e.unit, ms); this.rise(e.unit, BLESSED_TAG, '#ffe7a3', 1); }
        const u = e.kind === 'Healed' || e.kind === 'StatusSet' || e.kind === 'ResourceGained' || e.kind === 'MoveUsed' ? this.host.unit(e.unit) : undefined;
        if (u) this.scene.tweens.add({ targets: u, scaleX: 1.15, scaleY: 1.15, duration: ms / 2, yoyo: true });
        return done();
      }
      case 'sink': {
        if (e.kind !== 'Dropped') return done();
        const u = this.host.unit(e.unit);
        const away = u ? this.host.escape?.(e.unit) : undefined;
        if (u && away && this.host.escapeHost) { this.rise(e.unit, 'ESCAPES', FLOAT.push); return playEscape(this.host.escapeHost(speed), u, away); }
        // docs/56 EP5: the party's last hero topples slowly with the camera on it
        if (u && clip.finalFall) {
          this.host.finale?.(e.unit, ms);
          u.fall({ unit: e.unit, tile: { ...u.tile }, size: u.size, turn: 90, dx: 0, dy: 0 }, this.host.cell, ms * 0.55);
          return done();
        }
        // docs/50: a foe falls where it stood and lies there; only a body with nowhere to lie sinks
        if (u && e.lies) u.fall(e.lies, this.host.cell, ms);
        else if (u) this.scene.tweens.add({ targets: u.sprite, y: SINK_PX, alpha: 0, duration: ms });
        return done();
      }
      case 'unsink': {
        if (e.kind !== 'Revived') return done();
        const u = this.host.unit(e.unit);
        if (u) { this.scene.tweens.add({ targets: u.sprite, y: 0, alpha: 1, duration: ms }); this.rise(e.unit, 'STAGGERED', statusColour('Staggered')); u.aura.apply('Staggered', 1); }
        return done();
      }
      case 'banner': {
        if (e.kind === 'EventFired') this.host.banner(e.title, ms);
        return done();
      }
      case 'mechanism': {
        // docs/41: the lock's verdict over whoever gave the answer; words read; a block moved
        if (e.kind === 'Interacted') {
          const actor = clip.entry.actor;
          this.host.mechanism?.(e, ms, actor);
          if (actor !== undefined) {
            if (e.verdict === 'Accepted') this.rise(actor, 'IT OPENS', FLOAT.heal, 1);
            else if (e.verdict === 'Advanced') this.rise(actor, 'IT GLOWS', FLOAT.heal, 1);
            else if (e.verdict === 'Rejected') this.rise(actor, 'WRONG', FLOAT.hurt, 1);
            // docs/105: a Tally counts what stands right
            // docs/106: a touched well says how far off the true one is; a drawn bolt how many stand right
            else if (e.verdict === 'Near') this.rise(actor, e.verb === 'Touch' ? `${e.right ?? 0} AWAY` : `${e.right ?? 0} RIGHT`, FLOAT.hurt, 1);
            else if (e.read !== undefined) this.rise(actor, 'READ', FLOAT.status, 1);
          }
        }
        return done();
      }
      case 'reveal': {
        // docs/52: a glint on the square, and what it turned out to be rising from it
        if (e.kind === 'Revealed') {
          this.host.secret?.(e, ms);
          const name = this.host.secretName?.(e.fixture);
          this.host.floatText(e.tile, (name ?? 'something hidden').toUpperCase(), FLOAT.push, 1);
        }
        return done();
      }
      case 'trap': {
        if (e.kind === 'TrapSprung') {
          this.host.secret?.(e, ms);
          const u = this.host.unit(e.unit);
          if (u) { u.flash(90); this.scene.tweens.add({ targets: u, y: u.y - 4, duration: ms * 0.3, yoyo: true, ease: 'Quad.easeOut' }); }
          this.rise(e.unit, 'TRAP!', FLOAT.hurt, 1);
        }
        return done();
      }
      case 'flourish': {
        // docs/103 RB6: a flanked blow's moment — the striker coils, the friend across braces, two
        // daggers close on the foe and BACKSTAB! rises; the blow itself is the next clip
        if (e.kind !== 'Flanked') return done();
        const striker = this.host.unit(e.unit), foe = this.host.unit(e.target), friend = this.host.unit(e.with);
        const backstab = e.flank === 'backstab';
        if (striker && foe) {
          if (striker.x !== foe.x) striker.setFacing(foe.x > striker.x ? 1 : -1);
          // the second dagger comes from straight across the foe, where the friend stands (or is on its way to)
          this.host.combat?.flank(this.chest(foe), this.chest(striker), undefined, ms, backstab);
          const back = this.toward(striker, foe, -this.host.cell * 0.12);
          this.scene.tweens.add({ targets: striker, x: striker.x + back.x, y: striker.y + back.y, duration: Math.max(1, ms * 0.4), yoyo: true, ease: 'Quad.easeOut' });
        }
        if (friend && foe) {
          if (friend.x !== foe.x) friend.setFacing(foe.x > friend.x ? 1 : -1);
          friend.flash(120);
        }
        this.rise(e.target, backstab ? 'BACKSTAB! ×2' : 'FLANKED +2', FLOAT.backstab, 1);
        return done();
      }
      case 'interpose': {
        // docs/104 PD2: the paladin leaps to the friend's side in a streak of light, a shield flares
        // on the friend; the foe's blow then lands on the paladin (the next clip)
        if (e.kind !== 'Interposed') return done();
        const paladin = this.host.unit(e.unit), ward = this.host.unit(e.ward);
        if (paladin && ward) {
          this.host.combat?.interpose(this.chest(paladin), this.chest(ward), ms);
          if (paladin.x !== ward.x) paladin.setFacing(ward.x > paladin.x ? 1 : -1);
          const leap = this.toward(paladin, ward, Math.min(this.host.cell * 0.45, Math.hypot(ward.x - paladin.x, ward.y - paladin.y) * 0.4));
          this.scene.tweens.add({ targets: paladin, x: paladin.x + leap.x, y: paladin.y + leap.y, duration: Math.max(1, ms * 0.3), yoyo: true, hold: ms * 0.2, ease: 'Quad.easeOut' });
          ward.flash(90);
        }
        this.rise(e.unit, 'INTERPOSE!', FLOAT.radiant, 1);
        return done();
      }
      case 'hasten': {
        // docs/104 BD2: Encore — a clock spins back over the friend
        if (e.kind !== 'Hastened') return done();
        const u = this.host.unit(e.unit);
        if (u) {
          this.host.combat?.hasten({ x: u.x, y: u.y - Math.max(u.sprite.displayHeight, this.host.cell * 0.8) - this.host.cell * 0.2 }, ms);
          this.scene.tweens.add({ targets: u, scaleX: 1.12, scaleY: 1.12, duration: Math.max(1, ms * 0.25), yoyo: true, ease: 'Sine.easeInOut' });
        }
        this.rise(e.unit, `COOLDOWNS −${Math.max(1, e.turns)}`, FLOAT.tempo, 1);
        return done();
      }
      case 'react': {
        // docs/114 MT9: a row of the interaction table fires — its look over the squares it touched,
        // and its words over where it fired (matter fading on its own just redraws)
        if (e.kind !== 'Reacted' || e.rule === 0) return done();
        this.host.react?.(e, ms);
        return done();
      }
      case 'grow': {
        // docs/104 DR2: the land answers the druid on every square it changed
        if (e.kind !== 'TerrainSet') return done();
        this.host.combat?.grow(e.tiles.map((t) => this.host.tileCentre(t)), e.terrain === 'Thicket' ? 'Thicket' : e.terrain === 'Bad' ? 'Bad' : 'Open', ms);
        return done();
      }
      case 'callout': {
        // docs/60: the crawl's banners and call-outs (the host draws them; they hold on past the clip)
        if (e.kind === 'AreaEntered' || e.kind === 'LairWoke' || e.kind === 'Roused' || e.kind === 'LoreRead' || e.kind === 'ShrineAnswered') {
          this.host.crawl?.(e, ms);
          if (e.kind === 'LoreRead') this.rise(e.unit, 'LORE', FLOAT.status, 1);
          if (e.kind === 'Roused') { const u = this.host.unit(e.unit); if (u) { u.flash(120); this.rise(e.unit, '!', FLOAT.hurt, 1); } }
          if (e.kind === 'LairWoke') for (const id of e.units) { const u = this.host.unit(id); if (u) this.scene.tweens.add({ targets: u, y: u.y - 3, duration: Math.max(1, ms * 0.15), yoyo: true, repeat: 1, ease: 'Quad.easeOut' }); }
          if (e.kind === 'ShrineAnswered') for (const id of e.touched) { const u = this.host.unit(id); if (u) this.scene.tweens.add({ targets: u, scaleX: 1.12, scaleY: 1.12, duration: Math.max(1, ms * 0.25), yoyo: true, ease: 'Sine.easeInOut' }); }
        }
        // docs/66 RM10: the leader's call or raising — its line spoken over it, a banner on the
        // HUD; a broken leader calls no one, and the banner says so
        // docs/118 PT14a: the retreat — the leader's line in a bubble over it and the banner; it rears up as it shouts
        if (e.kind === 'Retreat') {
          this.host.call?.(e, ms);
          const u = e.leader !== undefined ? this.host.unit(e.leader) : undefined;
          if (u) this.scene.tweens.add({ targets: u, scaleX: 1.18, scaleY: 1.18, duration: Math.max(1, ms * 0.18), yoyo: true, repeat: 1, ease: 'Sine.easeInOut' });
        }
        if (e.kind === 'Called' || e.kind === 'Raised' || e.kind === 'CallBroken' || e.kind === 'RaiseBroken' || e.kind === 'CaptiveFreed') {
          this.host.call?.(e, ms);
          if (e.kind === 'Called' || e.kind === 'Raised') {
            const who = e.kind === 'Called' ? e.leader : e.caster;
            const u = this.host.unit(who);
            if (u) this.scene.tweens.add({ targets: u, scaleX: 1.15, scaleY: 1.15, duration: Math.max(1, ms * 0.2), yoyo: true, ease: 'Sine.easeInOut' });
          }
          if (e.kind === 'CaptiveFreed') this.rise(e.unit, 'NO LONGER HELD', FLOAT.status, 1);
        }
        // docs/61: exploration's moments (the host draws the card and the board's part of it)
        if (e.kind === 'Discovered' || e.kind === 'VeilParted' || e.kind === 'Glimpsed' || e.kind === 'WaySighted' || e.kind === 'Departed') {
          this.host.explore?.(e, ms);
          if (e.kind === 'Discovered') this.rise(e.unit, 'BEHELD', '#ffe7a3', 1);
          if (e.kind === 'Glimpsed') this.rise(e.unit, 'FAR SIGHT', FLOAT.status, 1);
        }
        return done();
      }
      case 'fall': {
        // docs/60 DC9: the floor gives — the figure shrinks into the hole, then drops into the cell
        if (e.kind !== 'Fell') return done();
        this.host.crawl?.(e, ms);
        const u = this.host.unit(e.unit);
        if (!u) return done();
        return new Promise<void>((resolve) => {
          const home = u.feetAt(e.from, this.host.cell);
          u.setPosition(home.x, home.y);
          this.scene.tweens.add({
            targets: u, scaleX: 0.2, scaleY: 0.2, alpha: 0, y: u.y + this.host.cell * 0.3, duration: Math.max(1, ms * 0.4), ease: 'Quad.easeIn',
            onComplete: () => {
              u.placeAt(e.to, this.host.cell);
              this.host.jumped?.(e.unit, e.from, e.to, ms * 0.6);
              const land = u.y;
              u.setScale(1).setAlpha(1).setY(land - this.host.cell * 0.9);
              this.scene.tweens.add({ targets: u, y: land, duration: Math.max(1, ms * 0.35), delay: ms * 0.1, ease: 'Bounce.easeOut', onComplete: () => { u.flash(90); if (u.size >= 2) this.host.thump?.(u.size, u); resolve(); } });
            },
          });
        });
      }
      case 'snare': {
        // docs/140: the board's trap layer draws it (the card, the camera, every act's own look)
        if (e.kind === 'SnareSprung' || e.kind === 'SnareActed' || e.kind === 'SnareEnded' || e.kind === 'SnareFound' || e.kind === 'SnareDisarmed') {
          this.host.snare?.(e, ms, clip.entry);
          if (e.kind === 'SnareFound') this.rise(e.unit, 'TRAP FOUND', FLOAT.push, 1);
          if (e.kind === 'SnareDisarmed') this.rise(e.unit, 'DISARMED', FLOAT.heal, 1);
          if (e.kind === 'SnareSprung' && e.victim !== undefined && e.by === undefined) { const u = this.host.unit(e.victim); if (u) { u.flash(110); this.scene.tweens.add({ targets: u, y: u.y - 5, duration: Math.max(1, ms * 0.12), yoyo: true, ease: 'Quad.easeOut' }); } }
        }
        return done();
      }
      case 'dissolve': case 'none': return done();
    }
  }

  /**
   * docs/102 FL2–FL5: a flier's move. It pushes off its square (dust, air, a flap), rises on an arc
   * and crosses to its new square as the crow flies — over the heads of everyone on the ground, its
   * shadow under it fading in as it climbs — and comes down there (a gust, dust, a soft thump). With
   * `carry` it seizes that figure as it rises, carries it hanging from its talons and lets it fall
   * onto the square the sim set it down on just before it lands itself.
   */
  private fly(u: UnitSprite, flight: NonNullable<Clip['flight']>, ms: number, blockedAt?: Tile): Promise<void> {
    const cell = this.host.cell;
    const { from, to, carry } = flight;
    const wings = flierOf(u.unit.appearance)?.wings ?? 'small';
    const peak = flightPeak(crowSquares(from, to), u.size, cell) * (carry ? CARRY_PEAK : 1);
    const fx = this.host.flight;
    const pan = this.host.panOf?.(u.unit.unitId);
    if (from.x !== u.tile.x || from.y !== u.tile.y) u.placeAt(from, cell);
    if (to.x !== from.x) u.setFacing(to.x > from.x ? 1 : -1);
    const shadow = fx?.shadow(u.size);
    fx?.takeOff(feetPoint(from, u.size, cell), u.size, wings, pan, u.visible);
    const held = carry ? this.host.unit(carry.unit) : undefined;
    const heldShadow = held ? fx?.shadow(held.size) : undefined;
    // a fallen figure is carried off as it lies
    const lies = held?.angle ?? 0;
    // it hangs from the talons: its head just under the carrier's feet
    const hang = cell * 0.75;
    let seized = false, dropped = false, landing = false;
    const st = { k: 0 };
    const pose = (k: number) => {
      const p = flightPose(k, from, to, u.size, cell, peak);
      u.setPosition(p.ground.x, p.ground.y - p.lift);
      u.setLift(p.lift);
      shadow?.place(p.ground, p.shadowAlpha, p.shadowScale);
      // halfway over, it is over its new square (what a square holds, where its call-outs rise)
      if (k >= 0.5 && (u.tile.x !== to.x || u.tile.y !== to.y)) u.tile = { ...to };
      if (!landing && k >= 0.84) { landing = true; fx?.land(feetPoint(to, u.size, cell), u.size, wings, pan, u.visible); }
      if (!held || !carry) return;
      const c = carriedPose(k, p.ground, p.lift, carry.from, carry.to, held.size, cell, hang);
      held.setPosition(c.x, c.y);
      held.setLift(c.lift);
      held.heldBy = c.grip > 0 ? u : undefined;
      // it dangles and swings under the talons
      held.setAngle(lies + Math.sin(k * Math.PI * 5) * 10 * c.grip);
      heldShadow?.place(c.ground, Math.min(1, c.lift / (cell * 0.6)) * 0.24, 1 - 0.2 * Math.min(1, c.lift / peak));
      if (!seized && k >= CARRY_GRAB_K + 0.04) {
        seized = true;
        held.flash(90);
        fx?.grab({ x: c.x, y: c.y - cell * 0.5 * held.size }, pan, held.visible);
        this.rise(held.unit.unitId, 'CARRIED OFF', FLOAT.push, 1);
      }
      if (!dropped && k >= CARRY_DROP_K) {
        dropped = true;
        held.placeAt(carry.to, cell);
        held.setAngle(lies);
        heldShadow?.destroy();
        held.flash(70);
        fx?.drop(feetPoint(carry.to, held.size, cell), this.host.panOf?.(held.unit.unitId), held.visible);
      }
    };
    return new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: st, k: 1, duration: ms, ease: 'Linear',
        onUpdate: () => pose(st.k),
        onComplete: () => {
          pose(1);
          shadow?.destroy();
          heldShadow?.destroy();
          u.placeAt(to, cell);
          if (held && carry && !dropped) { held.placeAt(carry.to, cell); held.setAngle(lies); }
          if (blockedAt) this.bump(u, blockedAt);
          if (u.size >= 2) this.host.thump?.(u.size, u);
          resolve();
        },
      });
    });
  }

  /**
   * docs/93 AQ8: the word over a shoved figure, and the water's part: a walker carried over the edge
   * into the deep goes SPLASH (its waterline throws up the big splash as it crosses); a floundering
   * friend hauled out by a Lend a Hand is HAULED OUT, the helper leaning back on the pull.
   */
  private shoveWord(e: Extract<Effect, { kind: 'Displaced' }>, u: UnitSprite, entry: ResolutionEntry, ms: number): string {
    const water = this.host.water;
    if (!water) return 'PUSH';
    const from = water.at(e.from), to = water.at(e.to);
    const swimmer = swims(gaitKey(u.unit.gait));
    if (from === 'deep' && to !== 'deep' && !swimmer) {
      const helper = entry.cause.kind === 'Intent' ? this.host.heroUnit?.(entry.cause.ref.heroId) : undefined;
      if (helper) {
        const back = this.toward(helper, u, -this.host.cell * 0.14);
        if (helper.x !== u.x) helper.setFacing(u.x > helper.x ? 1 : -1);
        this.scene.tweens.add({ targets: helper, x: helper.x + back.x, y: helper.y + back.y, angle: (u.x > helper.x ? -1 : 1) * 8, duration: Math.max(1, ms * 0.45), yoyo: true, ease: 'Quad.easeOut', onComplete: () => helper.setAngle(0) });
      }
      return 'HAULED OUT';
    }
    if (to === 'deep' && !swimmer) return 'SPLASH!';
    return 'PUSH';
  }

  /** A figure stepping through a stair or a portal stone: out where it stood, in at the far end. */
  private warp(u: UnitSprite, from: Tile, to: Tile, ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: u, alpha: 0, scaleX: 0.6, scaleY: 0.6, duration: Math.max(1, ms * 0.4), ease: 'Quad.easeIn',
        onComplete: () => {
          u.placeAt(to, this.host.cell);
          this.host.jumped?.(u.unit.unitId, from, to, ms * 0.6);
          this.scene.tweens.add({ targets: u, alpha: 1, scaleX: 1, scaleY: 1, duration: Math.max(1, ms * 0.45), delay: ms * 0.1, ease: 'Quad.easeOut', onComplete: () => resolve() });
        },
      });
    });
  }

  /**
   * docs/56 EP4: a melee blow — the attacker winds back, lunges a third of a square at the target,
   * worries at it (a quick wiggle along the line of the blow), then steps back onto its square.
   * The contact is at 35 %, a beat before the impact at 40 %.
   */
  private strike(attacker: UnitSprite, target: UnitSprite, ms: number): void {
    const home = { x: attacker.x, y: attacker.y };
    const fwd = this.toward(attacker, target, this.host.cell * LUNGE_SHARE);
    const back = this.toward(attacker, target, -this.host.cell * 0.1);
    const jig = this.toward(attacker, target, this.host.cell * 0.07);
    if (attacker.x !== target.x) attacker.setFacing(target.x > attacker.x ? 1 : -1);
    const tilt = Math.sign(target.x - attacker.x || 1) * 9;
    this.scene.tweens.chain({
      targets: attacker,
      tweens: [
        { x: home.x + back.x, y: home.y + back.y, angle: -tilt * 0.5, duration: ms * 0.15, ease: 'Quad.easeOut' },
        { x: home.x + fwd.x, y: home.y + fwd.y, angle: tilt, duration: ms * 0.2, ease: 'Quad.easeIn' },
        { x: home.x + fwd.x - jig.x, y: home.y + fwd.y - jig.y, angle: -tilt * 0.6, duration: ms * 0.06, ease: 'Sine.easeInOut' },
        { x: home.x + fwd.x + jig.x * 0.5, y: home.y + fwd.y + jig.y * 0.5, angle: tilt * 0.6, duration: ms * 0.06, ease: 'Sine.easeInOut' },
        { x: home.x + fwd.x - jig.x * 0.5, y: home.y + fwd.y - jig.y * 0.5, angle: -tilt * 0.3, duration: ms * 0.06, ease: 'Sine.easeInOut' },
        { x: home.x, y: home.y, angle: 0, duration: ms * 0.27, ease: 'Quad.easeInOut' },
      ],
    });
    // the target rocks back from the blow
    const push = this.toward(attacker, target, this.host.cell * 0.08);
    const tHome = { x: target.x, y: target.y };
    this.scene.time.delayedCall(ms * 0.38, () => {
      this.scene.tweens.add({ targets: target, x: tHome.x + push.x, y: tHome.y + push.y, duration: ms * 0.07, yoyo: true, ease: 'Quad.easeOut', onComplete: () => target.setPosition(tHome.x, tHome.y) });
    });
  }

  /** A blow lands on `unitId`: it flashes, bleeds away from whoever struck it, and its number rises. */
  private hurt(unitId: number, dealt: number, by: UnitSprite | undefined): void {
    const target = this.host.unit(unitId);
    target?.flash(110);
    if (target && dealt > 0) this.host.combat?.blood(this.chest(target), by ? this.chest(by) : undefined, dealt, target.y);
    this.rise(unitId, `-${dealt}`, FLOAT.hurt);
  }

  /** `len` board pixels from `a` toward `b` (negative: away). */
  private toward(a: { x: number; y: number }, b: { x: number; y: number }, len: number): { x: number; y: number } {
    const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy) || 1;
    return { x: (dx / d) * len, y: (dy / d) * len };
  }

  /** Where a figure is hit: about its chest (the container's origin is its feet). */
  private chest(u: UnitSprite): { x: number; y: number } { return { x: u.x, y: u.y - this.host.cell * 0.45 * u.size }; }

  private rise(unitId: number, text: string, colour: string, lift = 0): void {
    const u = this.host.unit(unitId);
    // docs/79: over the middle of a huge body's top row
    if (u) this.host.floatText(u.size > 1 ? { x: u.tile.x + (u.size - 1) / 2, y: u.tile.y } : u.tile, text, colour, lift);
  }

  private bump(u: UnitSprite, toward: Tile): void {
    const p = this.host.tileCentre(toward);
    const dx = Math.sign(p.x - u.x) * 2, dy = Math.sign(p.y - u.y) * 2;
    this.scene.tweens.add({ targets: u, x: u.x + dx, y: u.y + dy, duration: 60, yoyo: true });
  }
}

export { RISE_PX };
